/**
 * Render Engine
 * Handles dual-screen rendering for Nintendo DS
 */

export interface RenderConfig {
  topCanvas: HTMLCanvasElement;
  bottomCanvas: HTMLCanvasElement;
  scale?: number;
  smoothing?: boolean;
}

export class RenderEngine {
  private topCanvas: HTMLCanvasElement;
  private bottomCanvas: HTMLCanvasElement;
  private topCtx: CanvasRenderingContext2D;
  private bottomCtx: CanvasRenderingContext2D;
  private scale: number;

  // DS screen dimensions
  private readonly SCREEN_WIDTH = 256;
  private readonly SCREEN_HEIGHT = 192;

  // ImageData for faster rendering
  private topImageData: ImageData;
  private bottomImageData: ImageData;

  constructor(config: RenderConfig) {
    this.topCanvas = config.topCanvas;
    this.bottomCanvas = config.bottomCanvas;
    this.scale = config.scale || 2;

    // Setup canvases
    this.setupCanvas(this.topCanvas);
    this.setupCanvas(this.bottomCanvas);

    // Get 2D contexts
    const topCtx = this.topCanvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
    });
    const bottomCtx = this.bottomCanvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
    });

    if (!topCtx || !bottomCtx) {
      throw new Error('Failed to get 2D rendering contexts');
    }

    this.topCtx = topCtx;
    this.bottomCtx = bottomCtx;

    // Configure rendering
    const smoothing = config.smoothing !== undefined ? config.smoothing : false;
    this.topCtx.imageSmoothingEnabled = smoothing;
    this.bottomCtx.imageSmoothingEnabled = smoothing;

    // Create ImageData for both screens
    this.topImageData = this.topCtx.createImageData(this.SCREEN_WIDTH, this.SCREEN_HEIGHT);
    this.bottomImageData = this.bottomCtx.createImageData(this.SCREEN_WIDTH, this.SCREEN_HEIGHT);

    console.log(`✓ Render engine initialized (scale: ${this.scale}x)`);
  }

  /**
   * Setup canvas dimensions and style
   */
  private setupCanvas(canvas: HTMLCanvasElement): void {
    // Set internal resolution
    canvas.width = this.SCREEN_WIDTH;
    canvas.height = this.SCREEN_HEIGHT;

    // Set display size
    canvas.style.width = `${this.SCREEN_WIDTH * this.scale}px`;
    canvas.style.height = `${this.SCREEN_HEIGHT * this.scale}px`;

    // Pixel-perfect rendering
    canvas.style.imageRendering = 'pixelated';
    canvas.style.imageRendering = 'crisp-edges';
  }

  /**
   * Render a frame from the emulator framebuffer
   * @param framebuffer RGBA pixel data for both screens (256x192x2 screens)
   */
  renderFrame(framebuffer: Uint8Array): void {
    if (!framebuffer) {
      return;
    }

    const pixelsPerScreen = this.SCREEN_WIDTH * this.SCREEN_HEIGHT * 4; // RGBA

    // Split framebuffer into top and bottom screens
    const topScreenData = framebuffer.subarray(0, pixelsPerScreen);
    const bottomScreenData = framebuffer.subarray(pixelsPerScreen, pixelsPerScreen * 2);

    // Update ImageData
    this.topImageData.data.set(topScreenData);
    this.bottomImageData.data.set(bottomScreenData);

    // Draw to canvases
    this.topCtx.putImageData(this.topImageData, 0, 0);
    this.bottomCtx.putImageData(this.bottomImageData, 0, 0);
  }

  /**
   * Render both screens separately (for melonDS WASM)
   * @param topScreen RGBA pixel data for top screen (256x192)
   * @param bottomScreen RGBA pixel data for bottom screen (256x192)
   */
  renderScreens(topScreen: Uint8Array | null, bottomScreen: Uint8Array | null): void {
    // Render top screen
    if (topScreen && topScreen.length >= this.SCREEN_WIDTH * this.SCREEN_HEIGHT * 4) {
      this.topImageData.data.set(topScreen);
      this.topCtx.putImageData(this.topImageData, 0, 0);
    }

    // Render bottom screen
    if (bottomScreen && bottomScreen.length >= this.SCREEN_WIDTH * this.SCREEN_HEIGHT * 4) {
      this.bottomImageData.data.set(bottomScreen);
      this.bottomCtx.putImageData(this.bottomImageData, 0, 0);
    }
  }

  /**
   * Clear both screens
   */
  clear(): void {
    this.topCtx.fillStyle = '#000000';
    this.topCtx.fillRect(0, 0, this.SCREEN_WIDTH, this.SCREEN_HEIGHT);

    this.bottomCtx.fillStyle = '#000000';
    this.bottomCtx.fillRect(0, 0, this.SCREEN_WIDTH, this.SCREEN_HEIGHT);
  }

  /**
   * Draw loading/placeholder screen
   */
  drawPlaceholder(message: string = 'No ROM loaded'): void {
    this.clear();

    // Draw on top screen
    this.topCtx.fillStyle = '#1a1a1a';
    this.topCtx.fillRect(0, 0, this.SCREEN_WIDTH, this.SCREEN_HEIGHT);

    this.topCtx.fillStyle = '#ffffff';
    this.topCtx.font = '16px monospace';
    this.topCtx.textAlign = 'center';
    this.topCtx.textBaseline = 'middle';
    this.topCtx.fillText('Nintendo DS Emulator', this.SCREEN_WIDTH / 2, this.SCREEN_HEIGHT / 2 - 20);
    this.topCtx.fillText(message, this.SCREEN_WIDTH / 2, this.SCREEN_HEIGHT / 2 + 10);

    // Draw on bottom screen
    this.bottomCtx.fillStyle = '#1a1a1a';
    this.bottomCtx.fillRect(0, 0, this.SCREEN_WIDTH, this.SCREEN_HEIGHT);

    this.bottomCtx.fillStyle = '#666666';
    this.bottomCtx.font = '12px monospace';
    this.bottomCtx.textAlign = 'center';
    this.bottomCtx.textBaseline = 'middle';
    this.bottomCtx.fillText('Touch Screen', this.SCREEN_WIDTH / 2, this.SCREEN_HEIGHT / 2);
  }

  /**
   * Convert mouse coordinates to DS touch coordinates
   */
  mouseToTouchCoordinates(canvas: HTMLCanvasElement, mouseX: number, mouseY: number): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const scaleX = this.SCREEN_WIDTH / rect.width;
    const scaleY = this.SCREEN_HEIGHT / rect.height;

    const x = Math.floor((mouseX - rect.left) * scaleX);
    const y = Math.floor((mouseY - rect.top) * scaleY);

    return {
      x: Math.max(0, Math.min(this.SCREEN_WIDTH - 1, x)),
      y: Math.max(0, Math.min(this.SCREEN_HEIGHT - 1, y)),
    };
  }

  /**
   * Update scale
   */
  setScale(scale: number): void {
    this.scale = scale;
    this.setupCanvas(this.topCanvas);
    this.setupCanvas(this.bottomCanvas);
  }

  /**
   * Toggle image smoothing
   */
  setSmoothing(enabled: boolean): void {
    this.topCtx.imageSmoothingEnabled = enabled;
    this.bottomCtx.imageSmoothingEnabled = enabled;
  }

  /**
   * Get canvas dimensions
   */
  getDimensions() {
    return {
      width: this.SCREEN_WIDTH,
      height: this.SCREEN_HEIGHT,
      scale: this.scale,
    };
  }
}
